import { GhostButton, PageHero, PrimaryButton, Reveal, SectionTag } from "@/components/ui-bits";
import { DATA_AI_SECTIONS, DATA_AI_STACK, IMAGES } from "@/content/site";

const DataAI = () => (
  <>
    <PageHero
      eyebrow="Data & AI"
      lines={["Turn Data Into Intelligence.", "Turn AI Into Business Impact."]}
      sub="TARK helps organizations build modern data platforms, AI-powered applications and intelligent automation systems that transform information into measurable business value."
      image={IMAGES.dataAi}
    >
      <PrimaryButton to="/start-a-project" testId="dataai-talk-expert">Talk to Our Data &amp; AI Experts</PrimaryButton>
      <a href="#ai-capabilities" data-testid="dataai-explore-capabilities"
         className="inline-flex items-center rounded-full border border-tark-line px-7 py-3.5 font-display text-sm font-semibold text-tark-ink transition-colors hover:border-tark-cyan/50 hover:text-tark-cyan">
        Explore AI Capabilities
      </a>
    </PageHero>

    <section id="ai-capabilities" className="mx-auto max-w-7xl scroll-mt-24 px-6 pb-24 lg:px-10" data-testid="dataai-capabilities">
      <div className="grid gap-5 lg:grid-cols-2">
        {DATA_AI_SECTIONS.map((section, i) => (
          <Reveal key={section.title} delay={(i % 2) * 0.08}>
            <div className="card-glow h-full rounded-2xl border border-tark-line bg-tark-card p-8 sm:p-10"
                 data-testid={`dataai-section-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs text-tark-cyan">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{section.title}</h2>
              </div>
              <div className="mt-4 h-px w-16 bg-gradient-to-r from-tark-blue to-transparent" />
              <ul className="mt-7 grid gap-x-6 gap-y-3 sm:grid-cols-2">
                {section.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-tark-mut">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-tark-cyan/70" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="border-y border-tark-line bg-tark-surface/40 py-24" data-testid="ai-engineering-stack">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag>AI Engineering Stack</SectionTag>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            The Tools Behind the Intelligence
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {DATA_AI_STACK.map((group, i) => (
            <Reveal key={group.group} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-tark-line bg-tark-card p-6">
                <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-tark-cyan">{group.group}</h3>
                <ul className="mt-5 space-y-2.5">
                  {group.items.map((item) => (
                    <li key={item} className="text-sm leading-snug text-tark-mut">{item}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="dataai-outcomes">
      <Reveal className="rounded-2xl border border-tark-blue/40 bg-gradient-to-br from-tark-card to-[#0A1A33] p-10 sm:p-14">
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <SectionTag>From Data to Decisions</SectionTag>
            <h2 className="mt-3 max-w-xl font-display text-2xl font-bold sm:text-3xl">
              Ready to put your data to work?
            </h2>
            <p className="mt-3 max-w-xl text-sm text-tark-mut">
              Talk to TARK about data platforms, generative AI applications, AI agents and
              analytics engineered for measurable business impact.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <PrimaryButton to="/start-a-project" testId="dataai-bottom-cta">Talk to Our Data &amp; AI Experts</PrimaryButton>
            <GhostButton to="/services" testId="dataai-all-services">All Services</GhostButton>
          </div>
        </div>
      </Reveal>
    </section>
  </>
);

export default DataAI;
